import { ReactNode, useState } from 'react';
import { Box, CssBaseline, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { MainTopBar } from '../layout/MainTopBar';
import { UserThemeDialog } from '../settings/UserThemeDialog';
import { ExternalKeysDialog } from '../settings/ExternalKeysDialog';
import { FeedbackDialog } from '../support/FeedbackDialog';
import { HelpDialog } from '../support/HelpDialog';
import { useAuthStore } from '../../stores/authStore';
import { sendFeedback } from '../../services/feedback.service';
import { MainProfileSidebar, ProfileSection } from './MainProfileSidebar';

const drawerWidth = 360;

type MainProfileLayoutProps = {
  section: ProfileSection;
  headerLabel?: string;
  children: ReactNode;
};

export const MainProfileLayout = ({ section, headerLabel, children }: MainProfileLayoutProps) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [isExternalKeysDialogOpen, setIsExternalKeysDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const profileLabel = user?.name
    ? user.name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('')
    : '';

  const handleSendFeedback = async (comment: string) => {
    try {
      await sendFeedback(comment);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        bgcolor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <CssBaseline />

      <MainProfileSidebar
        drawerWidth={drawerWidth}
        selectedSection={section}
        onSelectHome={() => navigate('/main')}
        onSelectProfile={() => navigate('/profile')}
        onSelectBilling={() => navigate('/billing')}
        onSelectDelete={() => navigate('/delete')}
      />

      <Stack component="main" spacing={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <MainTopBar
          headerLabel={headerLabel}
          profileLabel={profileLabel}
          accountLevel={user?.accountLevel}
          canEditTheme={Boolean((user?.accountLevel ?? 0) > 0)}
          onOpenExternalKeys={() => {
            setIsExternalKeysDialogOpen(true);
          }}
          onOpenFeedback={() => {
            setIsFeedbackDialogOpen(true);
          }}
          onOpenHelp={() => {
            setIsHelpDialogOpen(true);
          }}
          onNavigateProfile={() => navigate('/profile')}
          onNavigateBilling={() => navigate('/billing')}
          onNavigateArchived={() => navigate('/archived')}
          onEditTheme={() => setIsThemeDialogOpen(true)}
          onLogout={() => {
            logout();
            navigate('/main');
          }}
        />

        <Stack sx={{ flexGrow: 1, m: '0px !important', px: 4, pt: 3, pb: 6, overflow: 'auto' }}>
          {children}
        </Stack>
      </Stack>

      <UserThemeDialog open={isThemeDialogOpen} onClose={() => setIsThemeDialogOpen(false)} />
      <ExternalKeysDialog
        open={isExternalKeysDialogOpen}
        onClose={() => setIsExternalKeysDialogOpen(false)}
      />
      <FeedbackDialog
        open={isFeedbackDialogOpen}
        onClose={() => setIsFeedbackDialogOpen(false)}
        onSendFeedback={handleSendFeedback}
      />
      <HelpDialog
        open={isHelpDialogOpen}
        onClose={() => setIsHelpDialogOpen(false)}
      />
    </Box>
  );
};
